import React, { useState, useMemo } from 'react';
import { Line, Bar } from 'react-chartjs-2'; // For visualization
import { FaHeart, FaSmile, FaChartLine, FaUsers } from 'react-icons/fa';

// --- Reusable Components ---

// 1. Engagement Score Card
const EngagementCard = ({ title, value, label, icon: Icon, colorClass }) => (
  <div className={`p-5 rounded-xl shadow-md transition duration-300 hover:shadow-lg bg-white border-l-4 ${colorClass}`}>
    <div className="flex items-center justify-between">
      <div className="text-sm font-semibold text-gray-700">{title}</div>
      <Icon className="text-2xl text-gray-500" />
    </div>
    <div className="mt-2 text-3xl font-bold text-gray-900">
      {value}
    </div>
    <div className="text-xs mt-1 text-gray-600">{label}</div>
  </div>
);

// 2. Engagement Domain Breakdown (Bar Chart Data)
const engagementDomains = {
  labels: ['Leadership', 'Work-Life Balance', 'Recognition', 'Career Growth', 'Compensation'],
  datasets: [
    {
      label: 'Favorable Score (%)',
      data: [75, 62, 85, 55, 70], // Scores out of 100
      backgroundColor: ['#48BB78', '#38A169', '#3182CE', '#D69E2E', '#ED8936'],
      borderRadius: 5,
    },
  ],
};

// --- Dummy Data ---
const mockEngagementData = {
  eNPS: 35, // Employee Net Promoter Score (Range: -100 to +100)
  favorableScore: '71%', // % of responses that were positive/favorable
  participation: '91%',
  highestScore: 'Recognition',
};

// Function to determine eNPS color
const geteNPSColor = (score) => {
  if (score >= 30) return 'border-green-500';
  if (score >= 0) return 'border-yellow-500';
  return 'border-red-500';
};

// --- Main Dashboard Component ---

const EngagementReports = () => {
  const data = mockEngagementData;

  // Data for the Engagement Trend (Line Chart)
  const engagementTrendData = {
    labels: ['Q1 2024', 'Q2 2024', 'Q3 2024', 'Q4 2024'],
    datasets: [
      {
        label: 'Employee Favorable Score (%)',
        data: [65, 68, 70, 71],
        fill: true,
        backgroundColor: 'rgba(59, 130, 246, 0.2)', // Light Blue
        borderColor: '#3B82F6', // Blue
        tension: 0.3,
        pointRadius: 6,
      },
    ],
  };

  // Dummy data for Department Breakdown Table
  const departmentBreakdown = [
    { department: 'Technology', score: '64%', eNPS: 15, risk: 'Medium' },
    { department: 'Sales', score: '82%', eNPS: 55, risk: 'Low' },
    { department: 'Finance', score: '70%', eNPS: 30, risk: 'Low' },
    { department: 'Operations', score: '58%', eNPS: 5, risk: 'High' },
  ];
  
  const getRiskColor = (risk) => {
    if (risk === 'High') return 'bg-red-100 text-red-800';
    if (risk === 'Medium') return 'bg-yellow-100 text-yellow-800';
    return 'bg-green-100 text-green-800';
  };

  return (
    <div className="min-h-screen p-4 sm:p-6 md:p-8 bg-gray-50" style={{ backgroundColor: '#F3F4F6' }}>
      <h1 className="text-3xl font-extrabold text-gray-800 mb-6 border-b pb-2">Employee Engagement Dashboard 🥰</h1>
      
      {/* 1. Key Metrics Section - Responsive Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <EngagementCard
          title="Employee Net Promoter Score (eNPS)"
          value={data.eNPS}
          label={data.eNPS >= 30 ? 'Excellent - Promoters outnumber Detractors' : 'Improvement needed'}
          icon={FaHeart}
          colorClass={`bg-white ${geteNPSColor(data.eNPS)}`}
        />
        <EngagementCard
          title="Overall Favorable Score"
          value={data.favorableScore}
          label="Percent of positive responses"
          icon={FaSmile}
          colorClass="bg-white border-blue-500"
        />
        <EngagementCard
          title="Survey Participation Rate"
          value={data.participation}
          label="Ensuring valid survey data"
          icon={FaUsers}
          colorClass="bg-white border-indigo-500"
        />
        <EngagementCard
          title="Highest Rated Domain"
          value={data.highestScore}
          label="Key strength of the organization"
          icon={FaChartLine}
          colorClass="bg-white border-green-500"
        />
      </div>

      {/* 2. Charts Section - Trend and Domain Breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        
        {/* Engagement Trend Chart (Line) */}
        <div className="bg-white p-6 rounded-xl shadow-lg">
          <h2 className="text-xl font-bold text-gray-800 mb-4">Engagement Favorable Score Trend</h2>
          <div className="h-80">
            <Line data={engagementTrendData} options={{ maintainAspectRatio: false, scales: { y: { beginAtZero: false, min: 50, max: 100 } } }} />
          </div>
        </div>
        
        {/* Engagement by Domain (Bar) */}
        <div className="bg-white p-6 rounded-xl shadow-lg">
          <h2 className="text-xl font-bold text-gray-800 mb-4">Engagement Score by Domain</h2>
          <div className="h-80">
            <Bar data={engagementDomains} options={{ maintainAspectRatio: false, indexAxis: 'y' }} />
          </div>
        </div>
      </div>

      {/* 3. Deep Dive - Departmental Breakdown Table */}
      <div className="bg-white p-6 rounded-xl shadow-lg">
        <h2 className="text-xl font-bold text-gray-800 mb-4">Engagement Breakdown by Department</h2>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Department</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Favorable Score</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">eNPS</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Risk Level</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Action</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {departmentBreakdown.map((item, index) => (
                <tr key={index} className="hover:bg-blue-50 transition duration-150">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{item.department}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{item.score}</td>
                  <td className={`px-6 py-4 whitespace-nowrap text-sm font-bold ${item.eNPS < 10 ? 'text-red-600' : 'text-green-600'}`}>{item.eNPS}</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getRiskColor(item.risk)}`}>
                      {item.risk}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <button className="text-indigo-600 hover:text-indigo-900">View Feedback</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default EngagementReports;